import { useState, useRef } from "react";
import { Upload, Link as LinkIcon, X, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface Props {
  label: string;
  hint?: string;
  value: string;
  onChange: (url: string) => void;
  bucket?: string;
  folder?: string;
  previewSize?: string;
}

const BrandingImageField = ({ label, hint, value, onChange, bucket = "restaurant-images", folder = "platform", previewSize = "w-16 h-16" }: Props) => {
  const { toast } = useToast();
  const [mode, setMode] = useState<"url" | "upload">(value && !value.startsWith("http") ? "upload" : "url");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast({ title: "Arquivo muito grande", description: "Máximo 2MB", variant: "destructive" });
      return;
    }
    setUploading(true);
    try {
      const ext = file.name.split(".").pop();
      const path = `${folder}/${crypto.randomUUID()}.${ext}`;
      console.log("[BrandingUpload] Uploading to bucket:", bucket, "path:", path);
      const { error } = await supabase.storage.from(bucket).upload(path, file, { upsert: true });
      if (error) {
        console.error("[BrandingUpload] Upload error:", error);
        toast({ title: "Erro no upload", description: error.message, variant: "destructive" });
      } else {
        const { data } = supabase.storage.from(bucket).getPublicUrl(path);
        console.log("[BrandingUpload] Public URL:", data.publicUrl);
        onChange(data.publicUrl);
        toast({ title: "Imagem enviada!" });
      }
    } catch (err) {
      console.error("[BrandingUpload] Exception:", err);
      toast({ title: "Erro inesperado", description: String(err), variant: "destructive" });
    }
    setUploading(false);
  };

  return (
    <div className="space-y-2">
      <Label className="text-xs">{label}</Label>
      <div className="flex items-start gap-3">
        {value && (
          <div className={`${previewSize} rounded-xl border border-border bg-muted/50 overflow-hidden shrink-0 flex items-center justify-center`}>
            <img src={value} alt={label} className="w-full h-full object-contain" onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }} />
          </div>
        )}
        <div className="flex-1 space-y-2">
          <div className="flex gap-1">
            <Button type="button" size="sm" variant={mode === "url" ? "default" : "outline"} className="h-7 text-xs" onClick={() => setMode("url")}>
              <LinkIcon className="h-3 w-3 mr-1" />URL
            </Button>
            <Button type="button" size="sm" variant={mode === "upload" ? "default" : "outline"} className="h-7 text-xs" onClick={() => setMode("upload")}>
              <Upload className="h-3 w-3 mr-1" />Upload
            </Button>
            {value && (
              <Button type="button" size="sm" variant="ghost" className="h-7 text-xs text-destructive" onClick={() => onChange("")}>
                <X className="h-3 w-3" />
              </Button>
            )}
          </div>
          {mode === "url" ? (
            <Input value={value} onChange={e => onChange(e.target.value)} className="h-8 text-xs" placeholder="https://exemplo.com/logo.png" />
          ) : (
            <div>
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleUpload} />
              <Button type="button" size="sm" variant="outline" className="h-8 text-xs w-full" disabled={uploading} onClick={() => fileRef.current?.click()}>
                {uploading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Upload className="h-3 w-3 mr-1" />}
                {uploading ? "Enviando..." : "Selecionar arquivo"}
              </Button>
            </div>
          )}
          {hint && <p className="text-[10px] text-muted-foreground">{hint}</p>}
        </div>
      </div>
    </div>
  );
};

export default BrandingImageField;
